import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import { api, type Site, type CreateSitePayload, type UpdateSitePayload } from '../../utils/api';

export type RequestStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface SitesListState {
  data: Site[];
  status: RequestStatus;
  error: string | null;
}

interface SiteDetailState {
  data: Site | null;
  status: RequestStatus;
  error: string | null;
}

interface SitesState {
  list: SitesListState;
  detail: SiteDetailState;
}

const initialState: SitesState = {
  list: {
    data: [],
    status: 'idle',
    error: null,
  },
  detail: {
    data: null,
    status: 'idle',
    error: null,
  },
};

const getErrorMessage = (error: unknown): string => {
  if (isAxiosError(error)) {
    const responseMessage =
      typeof error.response?.data === 'object'
        ? (error.response?.data as { message?: string })?.message
        : undefined;
    return responseMessage ?? error.message ?? 'Something went wrong.';
  }
  return error instanceof Error ? error.message : 'Something went wrong.';
};

export const fetchSitesThunk = createAsyncThunk<Site[], void, { rejectValue: string }>(
  'sites/fetchList',
  async (_, thunkAPI) => {
    try {
      const response = await api.get<Site[]>('/sites');
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const fetchSiteByIdThunk = createAsyncThunk<Site, string, { rejectValue: string }>(
  'sites/fetchById',
  async (siteId, thunkAPI) => {
    try {
      const response = await api.get<Site>(`/sites/${siteId}`);
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const createSiteThunk = createAsyncThunk<Site, CreateSitePayload, { rejectValue: string }>(
  'sites/create',
  async (payload, thunkAPI) => {
    try {
      const response = await api.post<Site>('/sites', payload);
      toast.success('Site added successfully.');
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const updateSiteThunk = createAsyncThunk<Site, { siteId: string; payload: UpdateSitePayload }, { rejectValue: string }>(
  'sites/update',
  async ({ siteId, payload }, thunkAPI) => {
    try {
      const response = await api.put<Site>(`/sites/${siteId}`, payload);
      toast.success('Site updated successfully.');
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const deactivateSiteThunk = createAsyncThunk<string, string, { rejectValue: string }>(
  'sites/deactivate',
  async (siteId, thunkAPI) => {
    try {
      await api.delete(`/sites/${siteId}`);
      toast.success('Site deactivated successfully.');
      return siteId;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const activateSiteThunk = createAsyncThunk<Site, string, { rejectValue: string }>(
  'sites/activate',
  async (siteId, thunkAPI) => {
    try {
      const response = await api.patch<Site>(`/sites/${siteId}/activate`);
      toast.success('Site reactivated successfully.');
      return response.data;
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      return thunkAPI.rejectWithValue(message);
    }
  },
);

const sitesSlice = createSlice({
  name: 'sites',
  initialState,
  reducers: {
    setSitesFromCache(state, action: PayloadAction<Site[]>) {
      state.list.data = action.payload;
      state.list.status = 'succeeded';
      state.list.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSitesThunk.pending, (state) => {
        state.list.status = 'loading';
        state.list.error = null;
      })
      .addCase(fetchSitesThunk.fulfilled, (state, action) => {
        state.list.status = 'succeeded';
        state.list.data = action.payload;
      })
      .addCase(fetchSitesThunk.rejected, (state, action) => {
        state.list.status = 'failed';
        state.list.error = action.payload ?? 'Unable to load sites.';
      })
      .addCase(fetchSiteByIdThunk.pending, (state) => {
        state.detail.status = 'loading';
        state.detail.error = null;
      })
      .addCase(fetchSiteByIdThunk.fulfilled, (state, action) => {
        state.detail.status = 'succeeded';
        state.detail.data = action.payload;
        const index = state.list.data.findIndex((site) => site._id === action.payload._id);
        if (index !== -1) {
          state.list.data[index] = action.payload;
        }
      })
      .addCase(fetchSiteByIdThunk.rejected, (state, action) => {
        state.detail.status = 'failed';
        state.detail.error = action.payload ?? 'Unable to load site.';
      })
      .addCase(createSiteThunk.fulfilled, (state, action) => {
        state.list.data = [action.payload, ...state.list.data];
        state.list.status = 'succeeded';
      })
      .addCase(createSiteThunk.rejected, (state, action) => {
        state.list.error = action.payload ?? 'Unable to create site.';
      })
      .addCase(updateSiteThunk.fulfilled, (state, action) => {
        state.detail.status = 'succeeded';
        state.detail.data = action.payload;
        const index = state.list.data.findIndex((site) => site._id === action.payload._id);
        if (index !== -1) {
          state.list.data[index] = action.payload;
        }
      })
      .addCase(updateSiteThunk.rejected, (state, action) => {
        state.detail.status = 'failed';
        state.detail.error = action.payload ?? 'Unable to update site.';
      })
      .addCase(deactivateSiteThunk.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter((site) => site._id !== action.payload);
        if (state.detail.data?._id === action.payload) {
          state.detail.data = null;
          state.detail.status = 'idle';
        }
      })
      .addCase(activateSiteThunk.fulfilled, (state, action) => {
        const index = state.list.data.findIndex((site) => site._id === action.payload._id);
        if (index !== -1) {
          state.list.data[index] = action.payload;
        } else {
          state.list.data = [action.payload, ...state.list.data];
        }
        if (state.detail.data?._id === action.payload._id) {
          state.detail.data = action.payload;
          state.detail.status = 'succeeded';
        }
      });
  },
});

export const { setSitesFromCache } = sitesSlice.actions;
export default sitesSlice.reducer;
